import { createContext, useContext, useEffect, useState } from "react";
import { DateTime } from "luxon";
import { DeviceState, DeviceStatus } from "./device.model";
import { useDeviceStatusesQuery } from "./device.queries";

export const DeviceStatusContext = createContext<{
  getDeviceState: (id: number) => DeviceStatus;
  getDevice: (id: number) => {
    status: DeviceStatus;
    version: string | null;
    lastOnlineAt: DateTime | null;
  };
  isLoading: boolean;
  getDeviceVersions: () => string[];
  isDeviceDisconnected: (id: number) => boolean;
  deviceStatusDict: Record<number, DeviceState>;
} | null>(null);

export const fallBackDeviceStatus = {
  status: DeviceStatus.Disconnected,
  version: null,
  lastOnlineAt: null,
};

const UPDATE_INTERVAL = 30000;

export const DeviceStatusProvider = ({ children }) => {
  const [deviceStatusDict, setDeviceStatus] = useState<
    Record<number, DeviceState>
  >({});
  const { data: deviceStatus, isLoading } = useDeviceStatusesQuery({
    refetchInterval: UPDATE_INTERVAL,
  });

  useEffect(() => {
    if (isLoading) return;

    /**
     * Transforming the device status array into a dictionary
     * where each key is the device ID and the value is its status.
     * This structure allows for quick lookups of a specific device's status
     * without needing to iterate through the entire array.
     */

    const ds = Object.fromEntries(
      (deviceStatus ?? []).map((s) => [s.id, s]),
    ) as Record<number, DeviceState>;

    setDeviceStatus(ds || {});
  }, [deviceStatus, isLoading]);

  const getDeviceState = (id: number) => {
    const device = deviceStatusDict[id] || fallBackDeviceStatus;
    return device.status;
  };

  const getDevice = (id: number) => {
    const device = deviceStatusDict[id] || fallBackDeviceStatus;
    return {
      status: device.status,
      version: device.version,
      lastOnlineAt: device.lastOnlineAt,
    };
  };

  const getDeviceVersions = () => {
    return Array.from(
      Object.values(deviceStatusDict).reduce((a, c) => {
        if (c.version != null) a.add(c.version);
        return a;
      }, new Set<string>()),
    ).sort();
  };

  const isDeviceDisconnected = (id: number) => {
    const deviceState = deviceStatusDict[id];
    // Don't return disconnected until the device status is actually known
    return !!deviceState && deviceState.status !== DeviceStatus.Connected;
  };
  return (
    <DeviceStatusContext.Provider
      value={{
        getDeviceState,
        getDevice,
        getDeviceVersions,
        isDeviceDisconnected,
        isLoading,
        deviceStatusDict,
      }}
    >
      {children}
    </DeviceStatusContext.Provider>
  );
};

export function useDeviceStateContext() {
  const value = useContext(DeviceStatusContext);
  if (!value) {
    throw new Error(
      "useDeviceStateContext has to be used under DeviceStatusProvider",
    );
  }
  return value;
}
